import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import {
  useAnalyticsIdentify,
  useSessionRecording,
} from '@/features/analytics/hooks';
import { isInInviteFlow } from '@/features/invites/utils/pendingInvite';
import { SentryIdentify } from '@/features/logging/components/sentryIdentify/sentryIdentify';
import { logger } from '@/features/logging/service/logger';
import { NotificationStreamHandler } from '@/features/notifications/components/notificationStreamHandler';
import { useFocusTracking } from '@/features/notifications/hooks/useFocusTracking';
import { getMyUserOptions } from '@/features/users/hooks/useGetMyUser/getMyUser.options';
import { customerInfoOptions } from '@/integrations/revenuecat/hooks/useCustomerInfo';
import { configureRevenueCat } from '@/integrations/revenuecat/utils/configureRevenueCat';
import {
  isForbiddenError,
  isUnauthorizedError,
} from '@/shared/utils/httpError';
import { isMobileWeb } from '@/shared/utils/mobileWeb';

export const Route = createFileRoute('/_protected')({
  component: Protected,
  beforeLoad: async ({ context, location }) => {
    // Redirect mobile web users to app store download page
    // Skip redirect if user is in invite flow (has pending invite token/data)
    if (
      isMobileWeb() &&
      !isInInviteFlow() &&
      location.pathname !== '/get-app'
    ) {
      throw redirect({ to: '/get-app', replace: true });
    }

    try {
      const { user } =
        await context.queryClient.ensureQueryData(getMyUserOptions());
      await configureRevenueCat(user.id);
    } catch (e: unknown) {
      if (!isUnauthorizedError(e) && !isForbiddenError(e)) {
        logger.error('Protected route beforeLoad error:', e);
        throw e;
      }
      // Auth error is expected for unauthenticated users, not a bug
      logger.warn('Protected route auth error, redirecting to login:', e);
      throw redirect({
        to: '/login',
        search: { redirectTo: location.href },
      });
    }
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(customerInfoOptions());
  },
});

function Protected() {
  useAnalyticsIdentify();
  useFocusTracking();
  useSessionRecording();

  return (
    <>
      <SentryIdentify />
      <NotificationStreamHandler />
      <Outlet />
    </>
  );
}
